import React, { useMemo } from "react";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Checkbox } from "primereact/checkbox";
import { useNavigate } from "react-router";
import C from "classnames";

import { LargePathSvg } from "./LargePathSvg";
import { SmallPathSvg } from "./SmallPathSvg";

import { ContentCardProps, AdminPathCardProps } from "../contentCardTypes";
import { LessonCard } from "../LessonCard/LessonCard";

import stackSvg from "../../../assets/icons/stack.svg";
import clockSvg from "../../../assets/icons/clock.svg";
import handsOnSvg from "../../../assets/icons/handson.svg";
import videoSvg from "../../../assets/icons/video.svg";

import { buildRoute } from "../../../../constants/routes";
import { isEmpty } from "../../../../utils";

import S from "./PathCard.module.scss";
import { PATH_CARD_SMALL_HEIGHT, PATH_CARD_SMALL_WIDTH } from "./PathCard.constants";
import { formatDuration } from "../../../utils";

export const PathCard: React.FC<ContentCardProps | AdminPathCardProps> = props => {
  const isAdminProps = (
    props: ContentCardProps | AdminPathCardProps
  ): props is AdminPathCardProps => {
    return "onActivate" in props || "onClone" in props;
  };

  const {
    contentType = "path",
    variant = "mauve",
    id,
    collectionKey,
    name,
    description,
    lessonsInEnrollment,
    handsOnLessonCount,
    videoLessonCount,
    estimatedTimeToComplete,
    cardSize = "large",
    nextLesson,
    showActivated = false,
    activated = false,
    contentTypeTag,
    disabled = false,
    pathType = "Path",
    status,
    catalogView = false,
    headingLevel: HeadingTag = "h3",
    passed,
    enrolled = false,
    // Admin-specific props (only available if isAdminProps is true)
    onActivate,
    onClone,
    isLoadingToggle,
    restricted,
  } = props as AdminPathCardProps;
  const navigate = useNavigate();

  const navigateToPath = (): void => {
    isAdminProps(props)
      ? navigate(buildRoute("Admin.routes.paths.routes.detail.route", { id: id }))
      : pathType === "Path"
        ? navigate(buildRoute("Learn.routes.path.route", { pathId: collectionKey || id }))
        : navigate(buildRoute("Learn.routes.quest.route", { pathId: collectionKey || id }));
  };

  const isComplete = passed || status === "passed";
  const isInProgress = status && ["inProgress", "in_progress"].includes(status);
  const isPaused = status && status === "abandoned";

  const buttonText = useMemo(() => {
    if (isAdminProps(props) || catalogView) return "View";
    if (isComplete) return "Revisit";
    if (isInProgress) return "Continue";
    if (isPaused) return "Resume";
    if (enrolled) return "Continue";
    return "View";
  }, [pathType, isComplete, isInProgress, isPaused, enrolled]);

  const nextHeadingLevel =
    `h${Math.min(Number(HeadingTag[1]) + 1, 6)}` as ContentCardProps["headingLevel"];

  const isLargeCard = cardSize === "large";
  const showLessonCard = isLargeCard && nextLesson && !isEmpty(nextLesson);
  const cardSizeClass = isLargeCard ? S.largeCard : S.smallCard;
  const variantClass = variant ? S[variant] : undefined;
  const isAdmin = isAdminProps(props);
  const showDescription =
    description && !isAdmin && (isLargeCard || (!estimatedTimeToComplete && !lessonsInEnrollment));
  const wrapperStyle = {
    "--path-card-small-width": `${PATH_CARD_SMALL_WIDTH}px`,
    "--path-card-small-height": `${PATH_CARD_SMALL_HEIGHT}px`,
  } as React.CSSProperties;

  return (
    <div
      className={C(S.pathCardWrapper, cardSizeClass, variantClass, "group")}
      style={wrapperStyle}
    >
      <div className={C(S.pathCardContainer, cardSizeClass, variantClass)}>
        {isLargeCard && <LargePathSvg className={S.largeLessonSvg} />}
        {!isLargeCard && <SmallPathSvg className={S.smallLessonSvg} />}
        <div className={C(S.detailsColumn, { [S.hasDetails]: showLessonCard })}>
          <div className="flex items-center gap-2">
            {!isAdmin && (
              <Tag
                value={isAdmin ? pathType || contentType : pathType}
                severity="contrast"
                rounded={true}
              />
            )}
            {!isLargeCard && enrolled && <Tag value="Enrolled" severity="info" rounded={true} />}
            {!isLargeCard && !enrolled && (isPaused || isInProgress) && (
              <Tag
                value={isPaused ? "Paused" : "in_progress"}
                severity="secondary"
                rounded={true}
              />
            )}
            {!isLargeCard && !enrolled && isComplete && (
              <Tag value="Completed" severity="success" rounded={true} />
            )}
            {contentTypeTag && (
              <Tag
                value={contentTypeTag?.replaceAll("_", " ")}
                severity="contrast"
                rounded={true}
              />
            )}
          </div>
          {isAdmin ? (
            <div className="relative w-full">
              <HeadingTag
                className={C(
                  isLargeCard ? `atlas-h1-alt white-text` : S.pathTitle,
                  "transition-all duration-600 ease-in-out group-hover:opacity-0 wrap-break-word"
                )}
              >
                {name}
              </HeadingTag>
              <p
                className={
                  "absolute inset-0 body-sm white-text opacity-0 group-hover:opacity-100 transition-all duration-600 ease-in-out wrap-break-word"
                }
              >
                {description?.length > 180 ? description?.slice(0, 180) + "..." : description}
              </p>
            </div>
          ) : (
            <HeadingTag className={C("atlas-h2-alt white-text", S.pathTitle)}>
              <span className={C(S.pathTitleInner, "wrap-break-word")}>{name}</span>
            </HeadingTag>
          )}
          {showDescription && (
            <p
              className={C(
                "white-text wrap-break-word",
                isLargeCard ? "body-md" : "body-md",
                S.pathDescription
              )}
            >
              {description}
            </p>
          )}
          {isLargeCard && (
            <Button label={buttonText} onClick={navigateToPath} disabled={disabled} />
          )}
          {(!!lessonsInEnrollment || !!estimatedTimeToComplete) && (
            <div className="flex flex-col gap-5">
              <div className={S.pathDetails}>
                {!!lessonsInEnrollment && (
                  <span className="inline-flex items-center gap-2 white-text">
                    <img src={stackSvg} alt="Lesson Stack Icon" />
                    {lessonsInEnrollment} lessons
                    {handsOnLessonCount || videoLessonCount ? (
                      <span className="inline-flex items-center gap-1">
                        (
                        {!!handsOnLessonCount && (
                          <span className="inline-flex items-center gap-1">
                            {handsOnLessonCount}
                            <img src={handsOnSvg} alt="Hands-on Icon" />
                          </span>
                        )}
                        {!!handsOnLessonCount && !!videoLessonCount && "+"}
                        {!!videoLessonCount && (
                          <span className="inline-flex items-center gap-1">
                            {videoLessonCount}
                            <img src={videoSvg} alt="Video Icon" />
                          </span>
                        )}
                        )
                      </span>
                    ) : null}
                  </span>
                )}
              </div>

              {!!estimatedTimeToComplete && (
                <div className="flex items-center gap-2">
                  <img src={clockSvg} alt="Clock Icon" />
                  <p className="white-text">{formatDuration(estimatedTimeToComplete)}</p>
                </div>
              )}
            </div>
          )}
          {!isLargeCard && (
            <Button label={buttonText} onClick={navigateToPath} disabled={disabled} />
          )}
        </div>
        {showLessonCard && (
          <div className={S.lessonColumn}>
            <p className="uppercase white-text">Up Next</p>
            <LessonCard
              {...nextLesson}
              title={nextLesson.title || nextLesson.name}
              contentType={nextLesson.lessonType || "video"}
              disabled={disabled}
              headingLevel={nextHeadingLevel}
              inPathCard={true}
            />
          </div>
        )}
        {/* Admin action buttons - only render when props are provided */}
        {isAdmin && (onActivate || onClone || restricted) && (
          <div className="absolute top-2 right-2 flex flex-col gap-1">
            {onActivate && (
              <Button
                type="button"
                id="toggle-button"
                severity="contrast"
                text
                icon={activated ? "pi pi-times" : "pi pi-check"}
                tooltip={activated ? "Deactivate" : "Activate"}
                tooltipOptions={{ position: "right" }}
                onClick={onActivate}
                className="z-20 text-white"
                loading={isLoadingToggle}
                disabled={isLoadingToggle}
              />
            )}
            {onClone && (
              <Button
                type="button"
                id="clone-button"
                severity="contrast"
                text
                icon="pi pi-copy"
                tooltip="Clone"
                tooltipOptions={{ position: "right" }}
                onClick={onClone}
                className="z-20 text-white"
                disabled={disabled}
              />
            )}
            {restricted && (
              <Button
                type="button"
                id="restricted-button"
                severity="contrast"
                // link instead of text to remove hover styles
                link
                icon="pi pi-list-check"
                tooltip="Restricted Path: If active, this path will only be available in Assignments and will not display on your learner's journey page."
                tooltipOptions={{ position: "top", className: "max-w-sm text-pretty" }}
                className="z-20 cursor-default text-white"
              />
            )}
          </div>
        )}
        {/* Existing checkbox behavior - only render when showActivated is true and no admin buttons */}
        {showActivated && !isAdmin && (
          <div className="absolute top-2 right-2">
            <Checkbox checked={activated} readOnly />
          </div>
        )}
      </div>
    </div>
  );
};
